import { AppNavParams } from '@euler/app/Routes';
import { AnnotationTagOption } from '@euler/app/flows/media/annotation/functions/AnnotationTagOption';
import { DefaultAnnotationTag } from '@euler/app/flows/media/annotation/functions/DefaultAnnotationTag';
import { useImagePreviewSize } from '@euler/app/flows/media/annotation/functions/useImagePreviewSize';
import { BlurView } from '@euler/components/BlurView';
import { Center } from '@euler/components/Center';
import { StatusColors } from '@euler/components/Colors';
import { Icon } from '@euler/components/Icomoon';
import { getImageMask } from '@euler/functions/getImageMask';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsMobileLayout } from '@euler/functions/responsiveness';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { sleep } from '@euler/utils';
import { usePersistFn } from '@euler/utils/hooks';
import { NavigationAction, useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { AnimatePresence, MotiView } from 'moti';
import {
  ReactNode,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  FlatList,
  Image,
  ListRenderItem,
  Platform,
  StyleProp,
  StyleSheet,
  TouchableOpacity,
  View,
  ViewStyle,
  useWindowDimensions,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  ImageAnnotationResult,
  ImageAnnotationView,
  ImageAnnotationViewRef,
} from './components/ImageAnnotationView';
import { TopActionBar } from './components/TopActionBar';
import { ImageAnnotationInput } from './types';

export const MultipleImageAnnotationScreen = wrapNavigatorScreen(
  ({
    inputs,
    tags,
    tagOptions,
    defaultTag,
    quality,
    watermark,
    onDone,
  }: {
    inputs: ImageAnnotationInput[];
    onDone?: (results: ImageAnnotationResult[]) => void;
    tags: string[];
    tagOptions?: AnnotationTagOption[];
    defaultTag?: DefaultAnnotationTag;
    quality?: number;
    watermark?: (scale?: number) => ReactNode;
  }) => {
    const navigation = useNavigation<StackNavigationProp<AppNavParams>>();
    const { bottom } = useSafeAreaInsets();
    const { width, height } = useWindowDimensions();
    const [currentIndex, setCurrentIndex] = useState(0);
    const isMobile = useIsMobileLayout();
    const [isNavigatingAway, setIsNavigatingAway] = useState(false);
    const continueAction = useRef<NavigationAction>();
    const annotationViewRef = useRef<ImageAnnotationViewRef>(null);
    const results = useRef(new Map<string, ImageAnnotationResult>()).current;
    const { rpx } = useResponsiveContext();
    const locking = useRef(false);

    useEffect(() => {
      return navigation.addListener('beforeRemove', e => {
        e.preventDefault();
        continueAction.current = e.data.action;
        annotationViewRef.current?.hideControls();
        setIsNavigatingAway(true);
      });
    }, [navigation]);

    const locked = useCallback(async (action: () => Promise<void>) => {
      if (locking.current) {
        return;
      }
      try {
        locking.current = true;
        await action();
      } finally {
        locking.current = false;
      }
    }, []);

    const onRetake = useCallback(() => {
      navigation.goBack();
    }, [navigation]);

    const onPrev = usePersistFn(async () => {
      await locked(async () => {
        if (annotationViewRef.current == null) return;
        const res = await annotationViewRef.current.getAnnotationResult();
        if (res == null) return;
        results.set(current.id, res);
        setCurrentIndex(x => x - 1);
        await sleep(150);
      });
    });

    const onNext = usePersistFn(async () => {
      await locked(async () => {
        if (annotationViewRef.current == null) return;
        const res = await annotationViewRef.current.getAnnotationResult();
        if (res == null) return;
        results.set(current.id, res);
        setCurrentIndex(x => x + 1);
        await sleep(150);
      });
    });

    const onConfirm = usePersistFn(async () => {
      await locked(async () => {
        if (annotationViewRef.current == null) return;
        const res = await annotationViewRef.current.getAnnotationResult();
        if (res == null) return;
        results.set(current.id, res);
        if (currentIndex === inputs.length - 1) {
          onDone?.([...results.values()]);
          await sleep(150);
        }
      });
    });

    const current = inputs[currentIndex];

    const mask = useMemo(
      () =>
        getImageMask({
          previewWidth: width,
          previewHeight: height,
          imageWidth: current.width,
          imageHeight: current.height,
        }),
      [current.height, current.width, height, width],
    );

    const onCellPress = usePersistFn(
      async (input: ImageAnnotationInput, index: number) => {
        if (index === currentIndex) return;
        if (results.get(input.id) == null) return;
        await locked(async () => {
          if (annotationViewRef.current == null) return;
          const res = await annotationViewRef.current.getAnnotationResult();
          if (res == null) return;
          results.set(current.id, res);
          setCurrentIndex(index);
        });
      },
    );

    const renderItem: ListRenderItem<ImageAnnotationInput> = useCallback(
      ({ item, index }) => {
        return (
          <Cell
            input={item}
            selected={index === currentIndex}
            active={results.get(item.id) != null}
            index={index}
            onPress={onCellPress}
            css={`
              margin-left: 8px;
              margin-right: 8px;
            `}
          />
        );
      },
      [currentIndex, onCellPress, results],
    );

    const [pw, ph] = useImagePreviewSize({
      mask,
      annotationMetadata: current.annotationMetadata,
      container: {
        width,
        height,
      },
    });

    const pixelScale = Math.min(pw, ph) / 375;

    return (
      <View
        css={`
          flex: 1;
          background-color: black;
        `}
      >
        <ImageAnnotationView
          imageUri={current.uri}
          key={current.id}
          containerWidth={width}
          containerHeight={height}
          width={pw}
          height={ph}
          mask={mask}
          watermark={watermark}
          tags={tags}
          tagOptions={tagOptions}
          defaultTag={
            results.get(current.id)?.annotationMetadata ? undefined : defaultTag
          }
          quality={quality}
          annotationMetadata={results.get(current.id)?.annotationMetadata}
          ref={annotationViewRef}
          topOffset={isMobile ? 0 : rpx(80)}
          tagButtonBarStyle={
            isMobile
              ? undefined
              : {
                  bottom: rpx(164) - bottom - rpx(15),
                  backgroundColor: 'transparent',
                }
          }
          css={`
            flex: 1;
          `}
        />

        <TopActionBar
          scale={pixelScale}
          blur={!isMobile}
          onPrev={onPrev}
          onNext={onNext}
          progressText={`${currentIndex + 1}/${inputs.length}`}
          canPrev={currentIndex > 0}
          canNext={currentIndex < inputs.length - 1}
          canDone={currentIndex === inputs.length - 1}
          onRetake={onRetake}
          onConfirm={onConfirm}
        />

        <AnimatePresence
          onExitComplete={() => {
            if (continueAction.current) {
              navigation.dispatch(continueAction.current);
            }
          }}
        >
          {!isNavigatingAway && (
            <MotiView
              from={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{
                type: 'timing',
              }}
              css={`
                width: 0px;
                height: 0px;
              `}
            />
          )}
        </AnimatePresence>

        {!isMobile && (
          <BlurView
            blurType="extraDark"
            css={`
              position: absolute;
              left: 0;
              right: 0;
              bottom: 0px;
              padding: ${rpx(32)}px 0;
              height: ${rpx(164)}px;
            `}
          >
            <FlatList
              data={inputs}
              keyExtractor={x => x.id}
              renderItem={renderItem}
              horizontal
              showsHorizontalScrollIndicator={Platform.OS === 'web'}
              contentContainerStyle={{
                flexGrow: 1,
                justifyContent: 'center',
              }}
            />
          </BlurView>
        )}
      </View>
    );
  },
  {
    headerShown: false,
    cardStyle: {
      backgroundColor: '#000000',
    },
    animationEnabled: false,
  },
);

const Cell = memo(
  ({
    index,
    input,
    active,
    selected,
    style,
    onPress,
  }: {
    index: number;
    input: ImageAnnotationInput;
    active?: boolean;
    selected?: boolean;
    style?: StyleProp<ViewStyle>;
    onPress?: (input: ImageAnnotationInput, index: number) => void;
  }) => {
    const { rpx } = useResponsiveContext();

    const onInternalPress = useCallback(() => {
      onPress?.(input, index);
    }, [input, index, onPress]);

    return (
      <TouchableOpacity
        css={`
          width: ${rpx(100)}px;
          height: ${rpx(100)}px;
        `}
        style={style}
        onPress={onInternalPress}
        disabled={!selected && !active}
      >
        <View
          style={StyleSheet.absoluteFill}
          css={`
            box-shadow: 0 0 ${rpx(50)}px #000;
          `}
        />
        <Image
          source={{ uri: input.uri }}
          css={`
            flex: 1;
            border-radius: ${rpx(15)}px;
            border-width: ${rpx(3)}px;
            border-color: ${selected ? '#fff' : 'transparent'};
            opacity: ${selected ? 1 : 0.75};
          `}
          resizeMode="cover"
        />
        {active && (
          <Center
            css={`
              position: absolute;
              top: ${rpx(4)}px;
              right: ${rpx(4)}px;
              width: ${rpx(20)}px;
              height: ${rpx(20)}px;
              border-radius: ${rpx(10)}px;
            `}
          >
            <Icon
              name="checkmark-circle"
              size={rpx(16)}
              color={StatusColors.Success}
            />
          </Center>
        )}
      </TouchableOpacity>
    );
  },
);
